import { AxiosResponse } from "axios";
import { useHTTP } from "../http";
import { Scope, Team, User } from "models";
import { permissionToString } from "pbHelpers/Permission";
import { pond } from "protobuf-ts/pond";
import { useGlobalState } from "../StateContainer";
import { createContext, PropsWithChildren, useContext } from "react";
import { objectQueryParams, pondURL } from "./pond";

export interface PermissionChanges {
  key: string; //the key of the object having its permissions changed (parent)
  permissions: string[] //the new permissions the parent will have to the child
}

export interface IPermissionAPIContext {
  getPermissions: (user: string, keys: string[], types: string[]) => Promise<AxiosResponse<pond.EvaluatePermissionsResponse>>;
  removePermissions: (user: string, scope: Scope) => Promise<AxiosResponse<any>>;
  updatePermissions: (scope: Scope, users: User[] | Team[]) => Promise<AxiosResponse<any>>;
  updateObjectPermissions: (key: string, type: string, parentType: string, changes: PermissionChanges[], keys: string[], types: string[]) => Promise<AxiosResponse<any>>;
  updateRelativePermissions: (
    parentScope: Scope,
    childScope: Scope,
    permissions: pond.Permission[]
  ) => Promise<AxiosResponse<any>>;
  shareObject: (
    scope: Scope,
    email: string,
    permissions: pond.Permission[],
    useImitation?: boolean
  ) => Promise<AxiosResponse<any>>;
  shareObjectByKey: (
    scope: Scope,
    key: string,
    kind: string,
    permissions: pond.Permission[],
    keys?: string[],
    type?: string[]
  ) => Promise<AxiosResponse<any>>;
  addShareableLink: (scope: Scope, expiration: string) => Promise<AxiosResponse<any>>;
  removeShareableLink: (scope: Scope, code: string) => Promise<AxiosResponse<any>>;
  listShareableLinks: (scope: Scope) => Promise<AxiosResponse<any>>;
  getShareableLink: (scope: Scope, code: string) => Promise<AxiosResponse<any>>;
}

export const PermissionAPIContext = createContext<IPermissionAPIContext>(
  {} as IPermissionAPIContext
);

interface Props {}

export default function PermissionProvider(props: PropsWithChildren<Props>) {
  const { children } = props;
  const { get, del, put, post } = useHTTP();
  const [{ as }] = useGlobalState();

  const getPermissions = (user: string, keys: string[], types: string[]) => {
    return new Promise<AxiosResponse>((resolve, reject) => {
      get<pond.EvaluatePermissionsResponse>(pondURL(
        "/users/" + user + "/permissions?types=" + types.toString() + "&keys=" + keys.toString() +
        (as && "&as=" + as)
      )).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };



  const removePermissions = (user: string, scope: Scope) => {
    return new Promise<AxiosResponse>((resolve, reject) => {
      del(pondURL("/users/" + user + "/permissions" + objectQueryParams(scope))).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };

  const updatePermissions = (scope: Scope, users: User[] | Team[]) => {
    interface user {
      id: string;
      permissions: string[];
    }
    interface request {
      users: user[];
    }
    let body: request = { users: [] };
    users.forEach((user: User | Team) => {
      body.users.push({
        id: user.id(),
        permissions: user.permissions.map(permission => permissionToString(permission))
      });
    });
    return new Promise<AxiosResponse>((resolve, reject) => {
      put(pondURL("/" + scope.kind + "s/" + scope.key + "/permissions"), body).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };

  const updateObjectPermissions = (key: string, type: string, parentType: string, changes: PermissionChanges[], keys: string[], types: string[]) => {
    let body = { 
      childKey: key,
      childType: type,
      parentType: parentType,
      permissionChanges: changes
    };

    if (as) {
      keys.unshift(as)
      types.unshift("team")
    }

    return new Promise<AxiosResponse>((resolve, reject) => {
      put(pondURL("/" + type + "s/" + key + "/objectPermissions?keys=" + keys + "&types=" + types ), body).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  }

  const shareObject = (
    scope: Scope,
    email: string,
    permissions: pond.Permission[],
    useImitation = true
  ) => {
    let sql = "/" + scope.kind + "s/" + scope.key + "/share";
    if (as && useImitation) sql = sql + "?as=" + as;
    return new Promise<AxiosResponse>((resolve, reject) => {
      post(pondURL(sql), {
        email: email,
        permissions: permissions.map(permission => permissionToString(permission))
      }).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };

  const updateRelativePermissions = (
    parentScope: Scope,
    childScope: Scope,
    permissions: pond.Permission[]
  ) => {
    let sql = "/updateRelativePermissions";
    if (as) sql = sql + "?as=" + as;
    return new Promise<AxiosResponse>((resolve, reject) => {

      put(pondURL(sql), {
        parent: parentScope.key,
        parentType: parentScope.kind,
        child: childScope.key,
        childType: childScope.kind,
        permissions: permissions.map(permission => permissionToString(permission))
      }).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };

  const shareObjectByKey = (scope: Scope, key: string, kind: string, permissions: pond.Permission[], keys?: string[], types?: string[]) => {
    let url = pondURL("/" + scope.kind + "s/" + scope.key + "/shareByKey") + (keys ? "?keys=" + keys.join(",") : "") +
        (types ? "&types=" + types.join(",") : "")
    if (as) url = pondURL(`/${scope.kind}s/${scope.key}/shareByKey?as=${as}`) + (keys ? "&keys=" + keys.join(",") : "") +
        (types ? "&types=" + types.join(",") : "")
    return new Promise<AxiosResponse>((resolve, reject) => {
      post(url, {
        key: key,
        kind: kind,
        permissions: permissions.map(permission => permissionToString(permission))
      }).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };

  const addShareableLink = (scope: Scope, expiration: string) => {
    let shareBody: pond.ShareableLinkSettings = pond.ShareableLinkSettings.create({
      expiration: expiration
    });
    return new Promise<AxiosResponse>((resolve, reject) => {
      return post(pondURL("/" + scope.kind + "s/" + scope.key + "/links"), shareBody).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };

  const removeShareableLink = (scope: Scope, code: string) => {
    return new Promise<AxiosResponse>((resolve, reject) => {
      del(pondURL("/" + scope.kind + "s/" + scope.key + "/links/" + code)).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };

  const listShareableLinks = (scope: Scope) => {
    return new Promise<AxiosResponse>((resolve, reject) => {
      get(pondURL("/" + scope.kind + "s/" + scope.key + "/links")).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
  };

  const getShareableLink = (scope: Scope, code: string) => {
    return new Promise<AxiosResponse>((resolve, reject) => {
      get(pondURL("/" + scope.kind + "s/" + scope.key + "/links/" + code)).then(resp => {
        return resolve(resp)
      }).catch(err => {
        return reject(err)
      })
    })
    
  };

  return (
    <PermissionAPIContext.Provider
      value={{
        getPermissions,
        removePermissions,
        updatePermissions,
        updateObjectPermissions,
        updateRelativePermissions,
        shareObject,
        shareObjectByKey,
        addShareableLink,
        removeShareableLink,
        listShareableLinks,
        getShareableLink
      }}>
      {children}
    </PermissionAPIContext.Provider>
  );
}

export const usePermissionAPI = () => useContext(PermissionAPIContext);
